import { type MetricId, METRIC_TOOLTIPS } from "./MetricTooltips.helpers";
// Task #316 — single source of truth for in-app metric explanations.
//
// Every place a built-in metric label is shown (KPI tile titles, chart
// legends, axis labels, column headers, panel headings) renders a
// ``<LabelWithTooltip metricId="..." />`` which prepends a tiny info
// indicator wired to the shared ``Tooltip`` primitive. Adding a new
// metric is one map entry — every surface picks it up.
//
// Custom (user-defined) measures don't live in this map. Surfaces that
// render them pass a ``description`` straight through to
// ``LabelWithTooltip``; if none exists the indicator is omitted so the
// label renders exactly as before.
import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { Tooltip } from "./Tooltip";

export interface LabelWithTooltipProps {
  // Either a known built-in metric id…
  metricId?: MetricId;
  // …or an ad-hoc description (used for custom user-defined measures
  // that carry their own help text). When both are provided
  // ``description`` wins, so callers can override a built-in copy if
  // ever needed.
  description?: string | null;
  // The label text rendered next to the info icon.
  children: ReactNode;
  // Visual className for the label text itself — lets callers preserve
  // table-header / KPI-tile typography.
  labelClassName?: string;
  // Wrapper className — defaults to ``inline-flex items-center gap-1``
  // which matches the existing label spacing in most surfaces.
  className?: string;
  // Test hook forwarded to the underlying Tooltip's popup element.
  "data-testid"?: string;
}

// Small wrapper: ``[label]  (i)`` where the icon is the focusable
// tooltip trigger. When no copy is available (unknown metricId and no
// description) we render the label plainly so the layout never shifts.
export function LabelWithTooltip({
  metricId,
  description,
  children,
  labelClassName,
  className,
  "data-testid": testId,
}: LabelWithTooltipProps) {
  const copy =
    description ?? (metricId ? METRIC_TOOLTIPS[metricId] : undefined);
  const wrapperCls = className ?? "inline-flex items-center gap-1";
  if (!copy) {
    return (
      <span className={wrapperCls}>
        <span className={labelClassName}>{children}</span>
      </span>
    );
  }
  const label =
    typeof children === "string" ? children : "metric explanation";
  return (
    <span className={wrapperCls}>
      <span className={labelClassName}>{children}</span>
      <Tooltip content={copy} data-testid={testId}>
        <button
          type="button"
          aria-label={`Explain ${label}`}
          className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          onClick={(e) => {
            // Don't bubble into clickable table rows / KPI tiles.
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          data-metric-info={metricId ?? "custom"}
        >
          <Info className="h-3 w-3" aria-hidden="true" />
        </button>
      </Tooltip>
    </span>
  );
}
